import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { createSharedComposable } from '@vueuse/core';
import psHttp from '@patsnap/common-ps-http';
import { handleLogout } from '@patsnap-utils/passport';
import { showPackageStatus } from '@patsnap-biz/package-status-dialog';
import { prepareTokenDeferred } from '@telecom/shared/startup/defer';
import { useUserInfo } from '@telecom/shared/domain/permission/composables/use-user-info';
import { EUREKA_SERVICE_URI, PASSPORT_URI } from '@telecom/shared/domain-config/config';

/** 切换租户成功响应（包入口未导出该类型，按使用字段本地声明） */
interface SwitchTenantResponse {
    token: string;
    refreshToken: string;
    replayUrl: string | null;
    errcode: string | null;
}

/**
 * 切换租户成功后应用新 Session 并跳转。
 *
 * 需清除本地 token / header 缓存，避免仍使用企业租户的 JWT；
 * 然后跳转到 replayUrl（passport 返回）或回退地址。
 * 等价于 f-eureka 的 applySwitchTenantSuccess。
 */
async function applySwitchTenantSuccess(res: SwitchTenantResponse, redirectUrl: string): Promise<void> {
    localStorage.removeItem('patsnap_header_account_info');
    try {
        const passport = await prepareTokenDeferred.promise;
        passport?.removeLocal();
    } catch (error) {
        console.error('[PackageStatusDialog] passport is not setup', error);
    }
    window.location.href = res.replayUrl || redirectUrl;
}

/**
 * 企业账号套餐状态弹窗
 *
 * 仅企业账号（company_id 存在）进入应用时触发，调用 showPackageStatus 弹窗，
 * 提醒套餐到期 / 引导切换或创建个人租户 / 联系管理员。
 *
 * 注意：与启动流程的 AddFreeService（产品准入检查）均会调用 need-add-trial，
 * 二者通过触发条件错开（启动流程对所有用户、本弹窗仅企业账号），与 f-eureka 现状一致。
 */
export const usePackageStatusDialog = createSharedComposable(() => {
    const { locale } = useI18n();
    const { isEnterpriseTenant } = useUserInfo();

    watch(
        () => isEnterpriseTenant.value,
        (isEnterprise) => {
            if (!isEnterprise) {
                return;
            }
            showPackageStatus({
                baseURL: EUREKA_SERVICE_URI,
                passportBaseURL: PASSPORT_URI,
                switchTenantClientId: import.meta.env.VITE_PASSPORT_CLIENT_ID,
                switchTenantFrom: 'eureka_home',
                http: psHttp,
                locale: locale.value as 'cn' | 'en' | 'jp' | 'tw',
                expiringThresholdDays: 3,
                onSwitchTenantSuccess: (res) => {
                    applySwitchTenantSuccess(res, window.location.href);
                },
                onSignInAnotherAccount: () => {
                    // 使用其他账号登录：登出后跳转完整登录页
                    handleLogout().then((data: unknown) => {
                        const { createLogoutHref } = data as {
                            createLogoutHref: (appendRedirect?: boolean, query?: object) => string;
                        };
                        const url = createLogoutHref(true, { anonymousLogin: false });
                        window.location.href = url;
                        window.location.reload();
                    });
                },
                onContactAdmin: () => {
                    // 新标签页打开联系管理员落地页（主域提供）
                    window.open('/contact-admin?pkg_type=ip_commercial', '_blank');
                },
            });
        },
        { immediate: true },
    );
});
